import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Users, Globe, DollarSign, TrendingUp, Plus, Building, MapPin, Mail, Phone,
  RefreshCw, Shield, BarChart2, UserPlus, Search, Loader2, Sparkles, Facebook,
  Instagram, Linkedin, Twitter, Trash2, Check, ArrowUpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useCurrency } from '../contexts/CurrencyContext';
import { Acronym } from '../components/shared/AcronymTooltip';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Philippines',
  'India', 'Singapore', 'United Arab Emirates', 'Germany', 'France',
  'Japan', 'South Korea', 'Brazil', 'Mexico', 'South Africa',
  'Nigeria', 'Kenya', 'Saudi Arabia', 'Indonesia', 'Thailand'
];

const StatCard = ({ title, value, icon: Icon, color = 'emerald-brand' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${typeof title === 'string' ? title.toLowerCase().replace(/\s+/g, '-') : 'metric'}`}>
    <CardContent className="p-3 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-gray-medium truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-heading font-bold text-text-primary mt-0.5">{value}</p>
        </div>
        <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0", `bg-${color}/10`)}>
          <Icon className={cn("h-5 w-5 sm:h-6 sm:w-6", `text-${color}`)} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AdminPortalPage = () => {
  const { user, effectiveRole } = useAuth();
  const { format: fc } = useCurrency();
  // Use effectiveRole for UI decisions (supports View As for super_admin)
  const isSuperAdmin = effectiveRole === 'super_admin';
  const isOperator = effectiveRole === 'country_operator';

  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [operators, setOperators] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);
  const [onboardWizardOpen, setOnboardWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [newOperator, setNewOperator] = useState({
    email: '', password: '', full_name: '', business_name: '', country: '', phone: ''
  });
  const [newClient, setNewClient] = useState({
    email: '', password: '', full_name: '', business_name: '', plan: 'starter', phone: '', industry: ''
  });
  const [wizardData, setWizardData] = useState({
    full_name: '', email: '', password: '', business_name: '', phone: '', industry: '', website: '', plan: 'starter',
    brand_voice: '', target_audience: '',
    social_accounts: [],
    create_welcome_automation: true
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/revenue`),
        axios.get(`${API_URL}/api/admin/clients`),
      ];
      if (isSuperAdmin) {
        requests.push(axios.get(`${API_URL}/api/admin/operators`));
      }
      const results = await Promise.all(requests);
      setStats(results[0].data);
      setRevenue(results[1].data);
      setClients(results[2].data.clients || []);
      if (isSuperAdmin && results[3]) {
        setOperators(results[3].data.operators || []);
      }
    } catch (error) {
      console.error('Admin fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOperator = async () => {
    if (!newOperator.email || !newOperator.password || !newOperator.full_name || !newOperator.country) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/operators`, newOperator);
      toast.success(`Operator created for ${newOperator.country}`);
      setCreateDialogOpen(false);
      setNewOperator({ email: '', password: '', full_name: '', business_name: '', country: '', phone: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create operator');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.email || !newClient.password || !newClient.full_name) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/operator/clients`, newClient);
      toast.success('Client created successfully');
      setCreateClientDialogOpen(false);
      setNewClient({ email: '', password: '', full_name: '', business_name: '', plan: 'starter', phone: '', industry: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleOnboardSubmit = async () => {
    if (!wizardData.email || !wizardData.full_name || !wizardData.password) {
      toast.error('Please fill required fields');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/onboard-client`, wizardData);
      toast.success(res.data.message);
      setOnboardWizardOpen(false);
      setWizardStep(0);
      setWizardData({
        full_name: '', email: '', password: '', business_name: '', phone: '', industry: '', website: '', plan: 'starter',
        brand_voice: '', target_audience: '', social_accounts: [], create_welcome_automation: true
      });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onboarding failed');
    } finally {
      setCreating(false);
    }
  };

  const addSocialToWizard = (platform) => {
    if (wizardData.social_accounts.find(a => a.platform === platform)) return;
    setWizardData(p => ({
      ...p,
      social_accounts: [...p.social_accounts, { platform, username: '' }]
    }));
  };

  const updateSocialUsername = (platform, username) => {
    setWizardData(p => ({
      ...p,
      social_accounts: p.social_accounts.map(a => a.platform === platform ? { ...a, username } : a)
    }));
  };

  const removeSocialFromWizard = (platform) => {
    setWizardData(p => ({
      ...p,
      social_accounts: p.social_accounts.filter(a => a.platform !== platform)
    }));
  };

  const handleDeactivateOperator = async (email) => {
    if (!window.confirm(`Deactivate ${email}?\n\nThey lose access immediately. You can reactivate later, or permanently delete once dormant.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/operators/${email}`);
      toast.success('Operator deactivated');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate operator');
    }
  };

  const handleReactivateOperator = async (email) => {
    try {
      await axios.post(`${API_URL}/api/admin/operators/${encodeURIComponent(email)}/reactivate`);
      toast.success('Operator reactivated');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate operator');
    }
  };

  const handleDeleteOperator = async (email) => {
    if (!window.confirm(
      `PERMANENTLY DELETE ${email}?\n\nThis is irreversible. Their user record, branding, and credit pool will be removed. Blocked if they still have active clients in their country.`
    )) return;
    try {
      await axios.delete(`${API_URL}/api/admin/operators/${encodeURIComponent(email)}?permanent=true`);
      toast.success('Operator permanently deleted');
      fetchAll();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete operator';
      // If blocked by active-client / not-inactive guard, offer force-delete as an escape hatch
      const canForce = error.response?.status === 409;
      if (canForce) {
        const forceOk = window.confirm(
          `${msg}\n\nFORCE DELETE anyway? This bypasses all safety checks and will delete the operator record even if they have active clients. Use this ONLY to clean up bad data.`
        );
        if (forceOk) {
          try {
            await axios.delete(`${API_URL}/api/admin/operators/${encodeURIComponent(email)}?permanent=true&force=true`);
            toast.success('Operator force-deleted');
            fetchAll();
            return;
          } catch (err2) {
            toast.error(err2.response?.data?.detail || 'Force delete failed');
            return;
          }
        }
      }
      toast.error(msg);
    }
  };

  // Promote a regular subscriber (client_admin) → country_operator. Super admin only.
  // If the user has no country set, prompts for one inline.
  const [promotingEmail, setPromotingEmail] = useState(null);

  const handlePromoteToOperator = async (client) => {
    const hasCountry = !!client?.country;
    const country = hasCountry
      ? client.country
      : (window.prompt(
          `Promote ${client.full_name || client.email} to a Country/Area Operator.\n\nWhich country or area should they operate? (e.g. Bahrain, Manila NCR, Riyadh)`,
          ''
        ) || '').trim();
    if (!country) return;
    const sure = window.confirm(
      `Promote ${client.email} to Country/Area Operator for "${country}"?\n\n• They keep all their existing data, login and whitelabel.\n• They'll gain operator privileges (manage clients, customise their landing page).\n• Their subdomain root will start showing the marketing landing instead of going straight to login.\n\nYou can revert this later from the Operators tab.`
    );
    if (!sure) return;
    setPromotingEmail(client.email);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${encodeURIComponent(client.email)}/promote-to-operator`,
        { country }
      );
      toast.success(`${client.email} is now a Country Operator for ${country}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromotingEmail(null);
    }
  };

  const handleDemoteFromOperator = async (operator) => {
    const sure = window.confirm(
      `Revert ${operator.email} from Country/Area Operator back to a regular subscriber?\n\nThey keep their data but lose operator privileges. Their subdomain root will go back to redirecting to login.`
    );
    if (!sure) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${encodeURIComponent(operator.email)}/demote-from-operator`
      );
      toast.success(`${operator.email} reverted to subscriber`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert user');
    }
  };

  // Change a user's email — for fixing placeholder/invalid addresses without
  // making the user click anything. Backend syncs whitelabel.owner_email and
  // invalidates any stale magic-link / password-reset tokens too.
  const handleChangeEmail = async (operator) => {
    const new_email = window.prompt(
      `Change login email for ${operator.full_name || operator.email}.\n\nCurrent email: ${operator.email}\n\nEnter the NEW email (it must be a real inbox they own — magic-link and password-reset emails will now go there):`,
      operator.email,
    );
    if (!new_email || new_email.trim().toLowerCase() === operator.email.toLowerCase()) return;
    if (!/^\S+@\S+\.\S+$/.test(new_email.trim())) {
      toast.error('That doesn\'t look like a valid email address.');
      return;
    }
    const sure = window.confirm(
      `Change ${operator.email} → ${new_email.trim().toLowerCase()}?\n\nThey will use the new email to log in from now on. Any pending sign-in links sent to the old address will stop working.`
    );
    if (!sure) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${encodeURIComponent(operator.email)}/change-email`,
        { new_email: new_email.trim().toLowerCase() },
      );
      toast.success(data.message || 'Email updated.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change email');
    }
  };

  const filteredClients = clients.filter(c =>
    !searchQuery ||
    c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-portal-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-text-primary">
            {isSuperAdmin ? 'Admin Portal' : `Operator Dashboard`}<PageInfoTooltip pageKey="admin-portal" />
          </h1>
          <p className="text-sm text-gray-medium">
            {isSuperAdmin 
              ? 'Manage operators, clients, and revenue' 
              : `Managing clients${user?.country ? ` in ${user.country}` : ''}`}
          </p>
        </div>
        {isSuperAdmin ? (
          <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2 w-full sm:w-auto" onClick={() => setCreateDialogOpen(true)} data-testid="create-operator-btn">
            <Plus className="h-4 w-4" />
            Add Operator
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateClientDialogOpen(true)} data-testid="create-client-btn">
              <UserPlus className="h-4 w-4 mr-1" />
              Quick Add
            </Button>
            <Button size="sm" className="bg-emerald-brand hover:bg-emerald-hover gap-1" onClick={() => setOnboardWizardOpen(true)} data-testid="onboard-wizard-btn">
              <Sparkles className="h-4 w-4" />
              Onboard
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Total Clients" value={stats?.total_clients || 0} icon={Users} />
        {isSuperAdmin && <StatCard title="Country Operators" value={stats?.total_operators || 0} icon={Globe} />}
        <StatCard title="Active Tenants" value={stats?.active_tenants || 0} icon={Building} />
        <StatCard title={<><Acronym term="MRR" /></>} value={fc(revenue?.mrr || 0)} icon={DollarSign} />
        {!isSuperAdmin && <StatCard title={<><Acronym term="ARR" /></>} value={fc(revenue?.arr || 0)} icon={TrendingUp} />}
      </div>

      {/* v2.9.0 — Pricing & Margin + Licensing Config quick-links (super-admin only) */}
      {isSuperAdmin && (
        <div className="flex justify-end gap-2">
          <a
            href="/admin/pricing-margin"
            className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
            data-testid="pricing-margin-quicklink"
          >
            <DollarSign className="w-4 h-4" />
            Pricing &amp; Margin
          </a>
          <a
            href="/admin/licensing-config"
            className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
            data-testid="licensing-config-quicklink"
          >
            <Globe className="w-4 h-4" />
            Licensing Config
          </a>
          <a
            href="/admin/voice-health"
            className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
            data-testid="voice-health-quicklink"
          >
            <Globe className="w-4 h-4" />
            Voice Health
          </a>
        </div>
      )}

      <Tabs defaultValue={isSuperAdmin ? "operators" : "clients"} className="space-y-4 sm:space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto">
          {isSuperAdmin && <TabsTrigger value="operators" className="text-xs sm:text-sm" data-testid="tab-operators">Operators</TabsTrigger>}
          <TabsTrigger value="clients" className="text-xs sm:text-sm" data-testid="tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="revenue" className="text-xs sm:text-sm" data-testid="tab-revenue">Revenue</TabsTrigger>
        </TabsList>

        {/* Operators Tab (Super Admin only) */}
        {isSuperAdmin && (
          <TabsContent value="operators" className="space-y-4">
            {operators.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {operators.map((op, i) => (
                  <Card key={i} className="bg-white border-gray-border overflow-hidden" data-testid={`operator-card-${op.email}`}>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-brand/10 flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-5 w-5 text-emerald-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 justify-between">
                            <p className="font-medium text-text-primary truncate">{op.full_name}</p>
                            <Badge className={cn(
                              "text-[10px] px-2 py-0.5 flex-shrink-0",
                              op.status === 'active' ? 'bg-emerald-brand text-white' : 'bg-red-500 text-white'
                            )}>
                              {op.status || 'active'}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-medium truncate">{op.email}</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-medium">
                          <Globe className="h-4 w-4" />
                          <span className="font-medium text-text-primary">{op.country}</span>
                        </div>
                        {op.business_name && (
                          <div className="flex items-center gap-2 text-gray-medium">
                            <Building className="h-4 w-4" />
                            <span>{op.business_name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-medium">
                          <Users className="h-4 w-4" />
                          <span>{op.client_count || 0} clients</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {op.status !== 'inactive' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50 text-xs min-w-[110px]"
                              onClick={() => handleChangeEmail(op)}
                              data-testid={`change-email-btn-${op.email}`}
                            >
                              Change Email
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-amber-600 border-amber-200 hover:bg-amber-50 text-xs min-w-[110px]"
                              onClick={() => handleDemoteFromOperator(op)}
                              data-testid={`demote-operator-btn-${op.email}`}
                            >
                              Revert to Subscriber
                            </Button>
                          </>
                        )}
                        {op.status === 'inactive' ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs min-w-[90px]"
                              onClick={() => handleReactivateOperator(op.email)}
                              data-testid={`reactivate-operator-btn-${op.email}`}
                            >
                              Reactivate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600 border-red-300 hover:bg-red-50 text-xs min-w-[120px]"
                              onClick={() => handleDeleteOperator(op.email)}
                              data-testid={`delete-operator-btn-${op.email}`}
                            >
                              Delete permanently
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-red-500 border-red-200 hover:bg-red-50 text-xs min-w-[90px]"
                            onClick={() => handleDeactivateOperator(op.email)}
                            data-testid={`deactivate-operator-btn-${op.email}`}
                          >
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white border-gray-border">
                <CardContent className="p-8 text-center">
                  <Globe className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-medium">No country operators yet</p>
                  <p className="text-sm text-gray-medium mb-4">Create operators to expand to new markets</p>
                  <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Country Operator
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-medium" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-clients-input"
              />
            </div>
          </div>
          {filteredClients.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block bg-white border border-gray-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-off-white border-b border-gray-border">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-medium">Name</th>
                        <th className="text-left p-3 font-medium text-gray-medium">Email</th>
                        <th className="text-left p-3 font-medium text-gray-medium">Business</th>
                        <th className="text-left p-3 font-medium text-gray-medium">Plan</th>
                        <th className="text-left p-3 font-medium text-gray-medium">Country</th>
                        {isSuperAdmin && <th className="text-right p-3 font-medium text-gray-medium">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((client, i) => (
                        <tr key={i} className="border-b border-gray-border last:border-0 hover:bg-off-white/50">
                          <td className="p-3 font-medium text-text-primary">{client.full_name}</td>
                          <td className="p-3 text-gray-medium">{client.email}</td>
                          <td className="p-3 text-gray-medium">{client.business_name || '—'}</td>
                          <td className="p-3">
                            <Badge className={cn(
                              "capitalize text-[10px]",
                              client.plan === 'enterprise' ? 'bg-purple-500 text-white' :
                              client.plan === 'growth' ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'
                            )}>
                              {client.plan || 'starter'}
                            </Badge>
                          </td>
                          <td className="p-3 text-gray-medium">{client.country || '—'}</td>
                          {isSuperAdmin && (
                            <td className="p-3 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handlePromoteToOperator(client)}
                                disabled={promotingEmail === client.email}
                                data-testid={`promote-to-operator-btn-${client.email}`}
                              >
                                {promotingEmail === client.email
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <ArrowUpCircle className="h-3 w-3" />}
                                {promotingEmail === client.email ? 'Promoting…' : 'Promote to Operator'}
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile card list */}
              <div className="sm:hidden space-y-2">
                {filteredClients.map((client, i) => (
                  <Card key={i} className="bg-white border-gray-border overflow-hidden">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-text-primary truncate">{client.full_name}</p>
                          <p className="text-xs text-gray-medium truncate">{client.email}</p>
                        </div>
                        <Badge className={cn(
                          "capitalize text-[10px] flex-shrink-0",
                          client.plan === 'enterprise' ? 'bg-purple-500 text-white' :
                          client.plan === 'growth' ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'
                        )}>
                          {client.plan || 'starter'}
                        </Badge>
                      </div>
                      {(client.business_name || client.country) && (
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-medium">
                          {client.business_name && <span>{client.business_name}</span>}
                          {client.country && <span>{client.country}</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">No clients found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card className="bg-white border-gray-border">
              <CardContent className="p-4 sm:p-5">
                <p className="text-xs sm:text-sm text-gray-medium">Monthly Recurring Revenue</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-emerald-brand mt-1">${(revenue?.mrr || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-medium mt-1">{revenue?.scope === 'global' ? 'Global' : revenue?.scope}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-border">
              <CardContent className="p-4 sm:p-5">
                <p className="text-xs sm:text-sm text-gray-medium">Annual Recurring Revenue</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mt-1">${(revenue?.arr || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-border">
              <CardContent className="p-4 sm:p-5">
                <p className="text-xs sm:text-sm text-gray-medium">Churn Rate</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mt-1">{revenue?.churn_rate || 0}%</p>
              </CardContent>
            </Card>
          </div>
          {revenue?.plan_breakdown && (
            <Card className="bg-white border-gray-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base sm:text-lg font-heading">Plan Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(revenue.plan_breakdown).map(([plan, count]) => (
                    <div key={plan} className="flex items-center justify-between p-2.5 sm:p-3 bg-off-white rounded-lg">
                      <span className="text-sm font-medium text-text-primary capitalize">{plan}</span>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm text-gray-medium">{count} clients</span>
                        <span className="text-sm font-semibold text-text-primary">
                          {fc(count * (plan === 'starter' ? 299 : plan === 'growth' ? 699 : 1499))}/mo
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Operator Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Country Operator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Full Name *</Label>
              <Input value={newOperator.full_name} onChange={(e) => setNewOperator(p => ({ ...p, full_name: e.target.value }))} className="mt-1" data-testid="operator-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newOperator.email} onChange={(e) => setNewOperator(p => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="operator-email-input" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={newOperator.password} onChange={(e) => setNewOperator(p => ({ ...p, password: e.target.value }))} className="mt-1" data-testid="operator-password-input" />
            </div>
            <div>
              <Label>Country *</Label>
              <Select value={newOperator.country} onValueChange={(val) => setNewOperator(p => ({ ...p, country: val }))}>
                <SelectTrigger className="mt-1" data-testid="operator-country-select">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Agency / Business Name</Label>
              <Input value={newOperator.business_name} onChange={(e) => setNewOperator(p => ({ ...p, business_name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={newOperator.phone} onChange={(e) => setNewOperator(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreateOperator} disabled={creating} data-testid="submit-operator-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Operator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog (for operators) */}
      <Dialog open={createClientDialogOpen} onOpenChange={setCreateClientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Full Name *</Label>
              <Input value={newClient.full_name} onChange={(e) => setNewClient(p => ({ ...p, full_name: e.target.value }))} className="mt-1" data-testid="client-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient(p => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="client-email-input" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={newClient.password} onChange={(e) => setNewClient(p => ({ ...p, password: e.target.value }))} className="mt-1" data-testid="client-password-input" />
            </div>
            <div>
              <Label>Business Name</Label>
              <Input value={newClient.business_name} onChange={(e) => setNewClient(p => ({ ...p, business_name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={newClient.plan} onValueChange={(val) => setNewClient(p => ({ ...p, plan: val }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter ($299/mo)</SelectItem>
                  <SelectItem value="growth">Growth ($699/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($1,499/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Industry</Label>
              <Input value={newClient.industry} onChange={(e) => setNewClient(p => ({ ...p, industry: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateClientDialogOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreateClient} disabled={creating} data-testid="submit-client-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Onboarding Wizard */}
      <Dialog open={onboardWizardOpen} onOpenChange={setOnboardWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-brand" />
              Client Onboarding Wizard
            </DialogTitle>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 my-4">
            {['Business Info', 'Brand & Audience', 'Social Accounts', 'Setup'].map((label, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium",
                  wizardStep >= i ? "bg-emerald-brand text-white" : "bg-gray-200 text-gray-medium"
                )}>
                  {wizardStep > i ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={cn("text-xs hidden sm:block", wizardStep >= i ? "text-text-primary" : "text-gray-medium")}>{label}</span>
                {i < 3 && <div className={cn("w-6 h-0.5", wizardStep > i ? "bg-emerald-brand" : "bg-gray-200")} />}
              </div>
            ))}
          </div>

          <div className="space-y-4 mt-2">
            {/* Step 0: Business Info */}
            {wizardStep === 0 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={wizardData.full_name} onChange={(e) => setWizardData(p => ({ ...p, full_name: e.target.value }))} className="mt-1" data-testid="wizard-name" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={wizardData.email} onChange={(e) => setWizardData(p => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="wizard-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={wizardData.password} onChange={(e) => setWizardData(p => ({ ...p, password: e.target.value }))} className="mt-1" data-testid="wizard-password" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={wizardData.phone} onChange={(e) => setWizardData(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Business Name</Label>
                    <Input value={wizardData.business_name} onChange={(e) => setWizardData(p => ({ ...p, business_name: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>Industry</Label>
                    <Input value={wizardData.industry} onChange={(e) => setWizardData(p => ({ ...p, industry: e.target.value }))} className="mt-1" placeholder="e.g., Restaurant, Dental, Law" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Website</Label>
                    <Input value={wizardData.website} onChange={(e) => setWizardData(p => ({ ...p, website: e.target.value }))} className="mt-1" placeholder="https://" />
                  </div>
                  <div>
                    <Label>Plan</Label>
                    <Select value={wizardData.plan} onValueChange={(v) => setWizardData(p => ({ ...p, plan: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter ($299/mo)</SelectItem>
                        <SelectItem value="growth">Growth ($699/mo)</SelectItem>
                        <SelectItem value="enterprise">Enterprise ($1,499/mo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Step 1: Brand & Audience */}
            {wizardStep === 1 && (
              <>
                <div>
                  <Label>Brand Voice</Label>
                  <Textarea
                    value={wizardData.brand_voice}
                    onChange={(e) => setWizardData(p => ({ ...p, brand_voice: e.target.value }))}
                    className="mt-2 min-h-[80px]"
                    placeholder="Describe the brand's tone: e.g., Professional yet friendly, uses simple language, avoids jargon..."
                    data-testid="wizard-brand-voice"
                  />
                </div>
                <div>
                  <Label>Target Audience</Label>
                  <Textarea
                    value={wizardData.target_audience}
                    onChange={(e) => setWizardData(p => ({ ...p, target_audience: e.target.value }))}
                    className="mt-2 min-h-[80px]"
                    placeholder="Who are their ideal customers? e.g., Small business owners aged 30-55 in the local area..."
                    data-testid="wizard-target-audience"
                  />
                </div>
              </>
            )}

            {/* Step 2: Social Accounts */}
            {wizardStep === 2 && (
              <>
                <p className="text-sm text-gray-medium">Connect the client's social media accounts:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
                    { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
                    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
                    { id: 'twitter', name: 'X/Twitter', icon: Twitter, color: '#000' },
                  ].map(p => {
                    const added = wizardData.social_accounts.find(a => a.platform === p.id);
                    return (
                      <Button
                        key={p.id}
                        variant={added ? "default" : "outline"}
                        size="sm"
                        className={added ? "bg-emerald-brand hover:bg-emerald-hover" : ""}
                        onClick={() => added ? removeSocialFromWizard(p.id) : addSocialToWizard(p.id)}
                      >
                        <p.icon className="h-4 w-4 mr-1.5" style={added ? {} : { color: p.color }} />
                        {p.name}
                      </Button>
                    );
                  })}
                </div>
                {wizardData.social_accounts.map(acc => (
                  <div key={acc.platform} className="flex items-center gap-3 p-3 bg-off-white rounded-lg">
                    <span className="text-sm font-medium capitalize w-24">{acc.platform}</span>
                    <Input
                      placeholder={`@${acc.platform}handle`}
                      value={acc.username}
                      onChange={(e) => updateSocialUsername(acc.platform, e.target.value)}
                      className="flex-1"
                    />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeSocialFromWizard(acc.platform)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {wizardData.social_accounts.length === 0 && (
                  <p className="text-sm text-gray-medium text-center py-4">Click a platform above to add it</p>
                )}
              </>
            )}

            {/* Step 3: Setup & Confirm */}
            {wizardStep === 3 && (
              <>
                <div className="bg-emerald-brand/5 border border-emerald-brand/20 rounded-xl p-4 space-y-3">
                  <h3 className="font-medium text-text-primary">Onboarding Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-medium">Name:</span> <strong>{wizardData.full_name}</strong></div>
                    <div><span className="text-gray-medium">Email:</span> <strong>{wizardData.email}</strong></div>
                    <div><span className="text-gray-medium">Business:</span> <strong>{wizardData.business_name || '—'}</strong></div>
                    <div><span className="text-gray-medium">Plan:</span> <strong className="capitalize">{wizardData.plan}</strong></div>
                    <div><span className="text-gray-medium">Social Accounts:</span> <strong>{wizardData.social_accounts.length}</strong></div>
                    <div><span className="text-gray-medium">Country:</span> <strong>{user?.country}</strong></div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-text-primary">Create Welcome Email Automation</p>
                    <p className="text-xs text-gray-medium">Auto-sends a 3-email welcome series to new subscribers</p>
                  </div>
                  <Switch
                    checked={wizardData.create_welcome_automation}
                    onCheckedChange={(v) => setWizardData(p => ({ ...p, create_welcome_automation: v }))}
                    data-testid="wizard-automation-switch"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="mt-4">
            {wizardStep > 0 && (
              <Button variant="outline" onClick={() => setWizardStep(s => s - 1)}>Back</Button>
            )}
            {wizardStep < 3 ? (
              <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setWizardStep(s => s + 1)} data-testid="wizard-next-btn">
                Next Step
              </Button>
            ) : (
              <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleOnboardSubmit} disabled={creating} data-testid="wizard-submit-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Complete Onboarding
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPortalPage;
